import { FC, ReactNode, useMemo } from "react";
import { Pressable, useWindowDimensions } from "react-native";
import * as DialogPrimitive from "@rn-primitives/dialog";
import { XIcon } from "lucide-react-native";

import { VnBox, VnThemedBox } from "@/components/VnBox";
import { useIsRTL } from "@/hooks/useIsRTL";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { useTranslate } from "@/hooks/useTranslate";
import { ChildrenOrI18nType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { VnButton } from "./VnButton/VnButton";
import { VnIcon } from "./VnIcon";
import { VnText } from "./VnText/VnText";

type VnSheetCloseButtonPropsType = {
  onPress?: () => void;
  className?: string;
};

export const VnSheetCloseButton: FC<VnSheetCloseButtonPropsType> = ({
  onPress,
  className,
}) => {
  return (
    <DialogPrimitive.Close asChild>
      <VnButton hitSlop={12} onPress={onPress} variant="ghost">
        <VnIcon
          as={XIcon}
          className={cn(
            "text-accent-foreground web:pointer-events-none size-5 shrink-0",
            className,
          )}
        />
      </VnButton>
    </DialogPrimitive.Close>
  );
};

type VnSheetPropsType = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  side?: "left" | "right" | "bottom";
  shouldShowCloseButton?: boolean;
  overlayClassName?: string;
  contentClassName?: string;
};

const SHEET_MAX_WIDTH = 496;

export const VnSheet: FC<VnSheetPropsType> = ({
  open,
  onOpenChange,
  children,
  side: sideProp,
  shouldShowCloseButton = true,
  overlayClassName,
  contentClassName,
}) => {
  const isRTL = useIsRTL();
  const defaultSide = useMemo(() => (isRTL ? "left" : "right"), [isRTL]);
  const side = sideProp ?? defaultSide;
  const { isDesktopWeb } = usePlatformOS();

  const { width } = useWindowDimensions();
  const sheetWidth =
    side === "bottom"
      ? width
      : width < SHEET_MAX_WIDTH
        ? width
        : SHEET_MAX_WIDTH;

  const close = () => {
    onOpenChange(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "animate-in fade-in-0 fixed inset-0 z-50 cursor-default",
            {
              "bg-black/80": isDesktopWeb,
              "bg-background": !isDesktopWeb,
            },
            overlayClassName,
          )}>
          <VnThemedBox className="h-full">
            {/* Overlay pressable area */}
            <Pressable
              className="absolute inset-0 cursor-default"
              onPress={close}
            />

            {/* Sheet Container */}
            <DialogPrimitive.Content
              className={cn(
                "border-border bg-background fixed z-50 flex cursor-default flex-col",
                side === "right" &&
                  "animate-in slide-in-from-right inset-y-0 right-0 border-l duration-300",
                side === "left" &&
                  "animate-in slide-in-from-left inset-y-0 left-0 border-r duration-300",
                side === "bottom" &&
                  "animate-in slide-in-from-bottom inset-x-0 bottom-0 border-t duration-300",
                contentClassName,
              )}
              style={{ width: sheetWidth }}>
              {shouldShowCloseButton && (
                <VnBox className="bg-background border-border h-[62px] items-end justify-center border-b px-2">
                  <VnSheetCloseButton onPress={close} />
                </VnBox>
              )}
              {children}
            </DialogPrimitive.Content>
          </VnThemedBox>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

type VnSheetContentPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSheetContent: FC<VnSheetContentPropsType> = ({
  children,
  className,
}) => {
  return (
    <VnBox className={cn("bg-background flex-1 p-6", className)}>
      {children}
    </VnBox>
  );
};

type VnSheetHeaderPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSheetHeader = ({
  children,
  className,
}: VnSheetHeaderPropsType) => {
  return <VnBox className={cn("gap-1.5 p-4", className)}>{children}</VnBox>;
};

type VnSheetTitlePropsType = {
  className?: string;
} & ChildrenOrI18nType;

export const VnSheetTitle: FC<VnSheetTitlePropsType> = ({
  children,
  i18nKey,
  i18nOptions,
  className,
}) => {
  return (
    <DialogPrimitive.Title className={className}>
      {children ?? (
        <VnText
          variant="headerTwo"
          i18nKey={i18nKey}
          i18nOptions={i18nOptions}
          className={cn("leading-none", className)}
        />
      )}
    </DialogPrimitive.Title>
  );
};

type VnSheetDescriptionPropsType = {
  className?: string;
} & ChildrenOrI18nType;

export const VnSheetDescription: FC<VnSheetDescriptionPropsType> = ({
  children,
  i18nKey,
  i18nOptions,
  className,
}) => {
  const i18nText = useTranslate(i18nKey, i18nOptions);
  return (
    <DialogPrimitive.Description
      className={cn("text-muted-foreground pt-2", className)}>
      {children ?? i18nText}
    </DialogPrimitive.Description>
  );
};

type VnSheetFooterPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSheetFooter: FC<VnSheetFooterPropsType> = ({
  children,
  className,
}) => {
  return (
    <VnBox
      className={cn(
        "border-border bg-background flex-row justify-end gap-2 border-t pt-4",
        className,
      )}>
      {children}
    </VnBox>
  );
};
